import { useState, useRef, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { authedFetch } from '@/lib/authedFetch';

interface Mensaje {
  id: string;
  rol: 'usuario' | 'asistente';
  contenido: string;
  timestamp: Date;
  fuentes?: string[];
}

interface Conversacion {
  id: string;
  titulo: string;
  fecha: Date;
  mensajes: Mensaje[];
}

export default function AdminAsistente() {
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [conversaciones, setConversaciones] = useState<Conversacion[]>([]);
  const [conversacionActiva, setConversacionActiva] = useState<Conversacion | null>(null);
  const [mensaje, setMensaje] = useState('');
  const [esperandoRespuesta, setEsperandoRespuesta] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preguntasSugeridas = [
    '¿Cuántos documentos se han procesado este mes?',
    '¿Cuántos clientes activos hay en el sistema?',
    '¿Cuántas incidencias abiertas hay?',
    '¿Cuál es el estado de la última sincronización con Cegid?',
    'Muéstrame el resumen global del sistema',
  ];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversacionActiva?.mensajes]);

  const handleEnviarMensaje = async (e: React.FormEvent | null, mensajeOverride?: string) => {
    if (e) e.preventDefault();
    const textoEnviar = mensajeOverride || mensaje;
    if (!textoEnviar.trim()) return;

    // Create or use conversation
    let conv = conversacionActiva;
    if (!conv) {
      conv = {
        id: Date.now().toString(),
        titulo: textoEnviar.substring(0, 50) + (textoEnviar.length > 50 ? '...' : ''),
        fecha: new Date(),
        mensajes: [],
      };
      setConversaciones(prev => [conv!, ...prev]);
      setConversacionActiva(conv);
    }

    const nuevoMensaje: Mensaje = {
      id: Date.now().toString(),
      rol: 'usuario',
      contenido: textoEnviar,
      timestamp: new Date(),
    };

    const convActualizada = { ...conv, mensajes: [...conv.mensajes, nuevoMensaje] };
    setConversacionActiva(convActualizada);
    setConversaciones(prev => prev.map(c => c.id === convActualizada.id ? convActualizada : c));
    setMensaje('');
    setEsperandoRespuesta(true);
    setError(null);

    try {
      const res = await authedFetch('/api/assistant/admin-chat', {
        method: 'POST',
        body: JSON.stringify({ mensaje: textoEnviar }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || 'Error al obtener respuesta');

      const respuestaIA: Mensaje = {
        id: (Date.now() + 1).toString(),
        rol: 'asistente',
        contenido: data.respuesta,
        timestamp: new Date(),
        fuentes: data.fuentes,
      };

      setConversacionActiva(prev => {
        if (!prev) return null;
        const actualizada = { ...prev, mensajes: [...prev.mensajes, respuestaIA] };
        setConversaciones(prevConv => prevConv.map(c => c.id === prev.id ? actualizada : c));
        return actualizada;
      });
    } catch (err: any) {
      setError(err.message || 'Error de conexión');
    } finally {
      setEsperandoRespuesta(false);
    }
  };

  const handleNuevaConversacion = () => {
    const nueva: Conversacion = {
      id: Date.now().toString(),
      titulo: 'Nueva conversación',
      fecha: new Date(),
      mensajes: [],
    };
    setConversaciones([nueva, ...conversaciones]);
    setConversacionActiva(nueva);
    setError(null);
  };

  const handlePreguntaSugerida = (pregunta: string) => {
    handleEnviarMensaje(null, pregunta);
  };

  return (
    <Layout rol="admin">
      <div className="h-[calc(100vh-72px)] flex gap-6 p-4 md:p-8">
        <div className="w-80 flex-shrink-0 space-y-4">
          <button
            onClick={handleNuevaConversacion}
            className="btn btn-admin w-full"
          >
            <span className="material-symbols-outlined text-sm align-middle">add</span> Nueva Conversación
          </button>

          <div className="card h-[calc(100%-4rem)] overflow-y-auto">
            <h3 className="font-light text-calm-text mb-3">Historial</h3>
            {conversaciones.length === 0 ? (
              <p className="text-xs text-calm-muted font-light">Sin conversaciones aún.</p>
            ) : (
              <div className="space-y-2">
                {conversaciones.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => setConversacionActiva(conv)}
                    className={`w-full text-left p-3 rounded-huge transition-colors ${
                      conversacionActiva?.id === conv.id
                        ? 'bg-admin/10 border-2 border-admin'
                        : 'bg-calm-bg hover:bg-calm-bg/80 border-2 border-transparent'
                    }`}
                  >
                    <div className="font-medium text-sm text-calm-text truncate">
                      {conv.titulo}
                    </div>
                    <div className="text-xs text-calm-muted mt-1">
                      {conv.fecha.toLocaleDateString('es-ES')}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col min-w-0">
          <div className="card mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-5xl font-light font-display text-calm-text">Asistente E2</h1>
                <p className="text-calm-muted text-sm mt-1">
                  Consulta datos reales del sistema — clientes, documentos, incidencias, sync, finanzas
                </p>
              </div>
              <span className="material-symbols-outlined text-4xl text-calm-accent">smart_toy</span>
            </div>
          </div>

          {(!conversacionActiva || conversacionActiva.mensajes.length === 0) && (
            <div className="card mb-4">
              <h3 className="font-light text-calm-text mb-3">Preguntas sugeridas:</h3>
              <div className="grid grid-cols-1 gap-2">
                {preguntasSugeridas.map((pregunta, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePreguntaSugerida(pregunta)}
                    disabled={esperandoRespuesta}
                    className="text-left p-3 bg-admin/5 hover:bg-admin/10 rounded-huge text-sm text-calm-text transition-colors disabled:opacity-50"
                  >
                    <span className="material-symbols-outlined text-sm align-middle mr-1">chat</span>
                    {pregunta}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex-1 card overflow-y-auto mb-4">
            <div className="space-y-4">
              {conversacionActiva?.mensajes.map(msg => (
                <div
                  key={msg.id}
                  className={`flex ${msg.rol === 'usuario' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-3xl rounded-huge p-4 ${
                      msg.rol === 'usuario'
                        ? 'bg-admin text-white'
                        : 'bg-calm-bg text-calm-text'
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{msg.contenido}</div>
                    {msg.fuentes && msg.fuentes.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-calm-border/30">
                        <div className="text-xs font-light mb-1 opacity-70">Datos:</div>
                        <div className="flex flex-wrap gap-2">
                          {msg.fuentes.map((fuente, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-white/20 px-2 py-1 rounded-huge"
                            >
                              <span className="material-symbols-outlined text-sm align-middle">database</span>{' '}
                              {fuente}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className={`text-xs mt-2 ${msg.rol === 'usuario' ? 'text-white/70' : 'text-calm-muted'}`}>
                      {msg.timestamp.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}

              {esperandoRespuesta && (
                <div className="flex justify-start">
                  <div className="bg-calm-bg rounded-huge p-4">
                    <div className="flex items-center space-x-2">
                      <div className="animate-bounce">●</div>
                      <div className="animate-bounce" style={{ animationDelay: '0.2s' }}>●</div>
                      <div className="animate-bounce" style={{ animationDelay: '0.4s' }}>●</div>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="flex justify-center">
                  <div className="bg-red-50 border border-red-200 rounded-huge p-3 text-sm text-red-600">
                    <span className="material-symbols-outlined text-sm align-middle mr-1">error</span>
                    {error}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          <form onSubmit={handleEnviarMensaje} className="card">
            <div className="flex gap-3">
              <input
                type="text"
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                placeholder="Pregunta sobre clientes, documentos, incidencias, financiero..."
                className="input flex-1"
                disabled={esperandoRespuesta}
              />
              <button
                type="submit"
                className="btn btn-admin"
                disabled={!mensaje.trim() || esperandoRespuesta}
              >
                Enviar
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
